import { InterfaceLimitationEnum } from '../../enums/InterfaceLimitationEnum';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import type RouterPage from '../../interfaces/RouterPage';
import type RouterPageContext from '../../interfaces/RouterPageContext';
import { envFlag } from '../../lib/envFlag';

import { hasInterfaceLimitation } from '../utils';

export const routerPages: RouterPage[] = [
  {
    hasDivider: false,
    href: '/files/personal',
    icon: 'FolderIcon',
    isDisabled: false,
    isHidden: false,
    key: 'files',
    label: 'common.files',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowFiles,
      );
    },
  },
  {
    hasDivider: false,
    href: '/files/received',
    icon: 'FileDownIcon',
    isDisabled: false,
    isHidden: false,
    key: 'received',
    label: 'common.received',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowReceived,
      );
    },
  },
  {
    hasDivider: false,
    href: '/files/shared',
    icon: 'FileUserIcon',
    isDisabled: false,
    isHidden: false,
    key: 'shared',
    label: 'common.shared',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowShared,
      );
    },
  },
  {
    hasDivider: false,
    href: '/files/favorites',
    icon: 'StarIcon',
    isDisabled: false,
    isHidden: false,
    key: 'favorites',
    label: 'common.favorites',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowFavorites,
      );
    },
  },
  {
    hasDivider: false,
    href: '/files/recent',
    icon: 'HistoryIcon',
    isDisabled: false,
    isHidden: false,
    key: 'recent',
    label: 'common.recent',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyListRecentFiles,
      );
    },
  },
  {
    hasDivider: false,
    href: '/files/hidden',
    icon: 'ArchiveIcon',
    isDisabled: false,
    isHidden: false,
    key: 'archive',
    label: 'common.hidden',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowHidden,
      );
    },
  },
  {
    hasDivider: true,
    href: '/files/trash',
    icon: 'TrashIcon',
    isDisabled: false,
    isHidden: false,
    key: 'trash',
    label: 'common.trash',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowTrash,
      );
    },
  },
  {
    hasDivider: false,
    href: '/users',
    icon: 'UserIcon',
    isDisabled: false,
    isHidden: false,
    key: 'users',
    label: 'common.users',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowUsers,
      );
    },
  },
  {
    hasDivider: true,
    href: '/groups',
    icon: 'UsersIcon',
    isDisabled: false,
    isHidden: false,
    key: 'groups',
    label: 'common.groups',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowGroups,
      );
    },
  },
  {
    hasDivider: false,
    href: '/activity-logs',
    icon: 'LogsIcon',
    isDisabled: false,
    isHidden: false,
    key: 'activity-log',
    label: 'common.activityLog',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowActivities,
      );
    },
  },
  {
    hasDivider: false,
    href: '/antivirus-quarantine',
    icon: 'ShieldBanIcon',
    isDisabled: false,
    isHidden: false,
    key: 'antivirus-quarantine',
    label: 'pageTitle.antivirusQuarantine',
    notificationsCount: 0,
    // Admin tool (not a setting): licence + role. Page also respects AntivirusScan deny.
    condition: (context) => {
      return (
        [UserRoleEnum.Admin, UserRoleEnum.SuperAdmin].includes(
          context.authenticatedUser?.role || UserRoleEnum.Reader,
        ) &&
        (context.authenticatedUser?.isAntivirusEnabled ?? false)
      );
    },
  },
  {
    hasDivider: true,
    href: '/dashboard',
    icon: 'LayoutDashboardIcon',
    isDisabled: false,
    isHidden: false,
    key: 'dashboard',
    label: 'dashboard.title',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowExecutiveReport,
      );
    },
  },
  {
    hasDivider: true,
    href: '/sales-dashboard',
    icon: 'StoreIcon',
    isDisabled: false,
    isHidden: false,
    key: 'sales-dashboard',
    label: 'store.salesDashboard',
    notificationsCount: 0,
    condition: (context) => {
      return (
        [UserRoleEnum.Admin, UserRoleEnum.SuperAdmin].includes(
          context.authenticatedUser?.role || UserRoleEnum.Reader,
        ) &&
        envFlag(import.meta.env.VITE_APP_STORE_ENABLED) &&
        (context.authenticatedUser?.isSellEnabled || false)
      );
    },
  },
  {
    hasDivider: true,
    href: '/sharepoint',
    icon: 'FolderSyncIcon',
    isDisabled: false,
    isHidden: false,
    key: 'sharepoint',
    label: 'sharePointStatus.menuLabel',
    notificationsCount: 0,
    // Administrators and SuperAdmins only, and only when the licence flag is on: every
    // /api/sharepoint/* route answers 403 otherwise. Interface deny hides the menu entry.
    condition: (context) => {
      return (
        [UserRoleEnum.Admin, UserRoleEnum.SuperAdmin].includes(
          context.authenticatedUser?.role || UserRoleEnum.Reader,
        ) &&
        context.authenticatedUser?.isSharePointGrantEnabled === true &&
        !hasInterfaceLimitation(
          context.authenticatedUser?.settings || [],
          InterfaceLimitationEnum.DenyShowSharePoint,
        )
      );
    },
  },
  {
    hasDivider: true,
    href: '/settings',
    icon: 'SettingsIcon',
    isDisabled: false,
    isHidden: false,
    key: 'settings',
    label: 'common.settings',
    notificationsCount: 0,
    condition: (context) => {
      return !hasInterfaceLimitation(
        context.authenticatedUser?.settings || [],
        InterfaceLimitationEnum.DenyShowSettings,
      );
    },
  },
];

/**
 * Filters the router pages based on the context.
 * @param context RouterPageContext
 * @returns RouterPage[]
 */
export function filteredRouterPages(context: RouterPageContext): RouterPage[] {
  return routerPages.filter((page) => {
    if (page.condition) {
      return page.condition(context);
    }
    return true;
  });
}
